import { mapValues, random } from "lodash-es";
import { nextTick } from "vue";

import { internalActions } from "@/action/internalActions";
import { cardSizeFrom } from "@/board/board-utils";
import { sendBoardLoaded, useEventBus } from "@/composables/useEventBus";
import { CancelError } from "@/error/CancelError";
import { reload } from "@/error/reload";
import { addBreadcrumb, captureException, captureMessage } from "@/error/sentry";
import { SECONDS } from "@/model/Settings";
import type { StickerType } from "@/model/StickerType";
import type { BoardType, IdMap } from "@/model/baseTypes";
import { almTypeInfo } from "@/model/baseTypes";
import type {
  Board,
  BoardData,
  BoardIteration,
  BoardState,
  BoardWithObjectives,
  Kanban,
} from "@/model/board";
import {
  MAX_FINISHED_TICKS,
  assureBoardType,
  hasBoardObjectives,
  isBacklogBoard,
  isFrontendBoard,
} from "@/model/board";
import type { Card, SearchResult } from "@/model/card";
import type { Category, FlexType } from "@/model/flexboard";
import type { Link } from "@/model/link";
import type { TreeSticky } from "@/model/objective";
import type { Session } from "@/model/session";
import type { AlmStickyType, StickyType } from "@/model/stickyType";
import type { EventFilter } from "@/store/activity";
import { useAlmItemTypeStore } from "@/store/almItemType";
import { useArtStore } from "@/store/art";
import { isEmptyBoard, useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import { useCardStore } from "@/store/card";
import { useCompanySettingsStore } from "@/store/companySettings";
import { useConnectionStore } from "@/store/connection";
import { useIterationStore } from "@/store/iteration";
import { useLicenseStore } from "@/store/license";
import { useLoadingStore } from "@/store/loading";
import { useNavigationStore } from "@/store/navigation";
import { useServerSettingsStore } from "@/store/serverSettings";
import { useSessionStore } from "@/store/session";
import { statsProvider, useStatisticsStore } from "@/store/statistics";
import { isZombieType, useStickyTypeStore } from "@/store/stickyType";
import { useTeamStore } from "@/store/team";
import type Resolvable from "@/utils/async/Resolvable";
import Scheduler from "@/utils/async/Scheduler";
import { timeoutHandling } from "@/utils/async/timeoutHandling";
import {
  fontsLoaded,
  isBrowserTabActive,
  resolveBrowserTabVisible,
  untilBrowserTabVisible,
} from "@/utils/dom/dom";

import { DataError } from "../DataError";
import { retrieveBackendSession } from "../impl/backendSessionCache";
import type { ChartScope } from "../natsstubs/session/models";
import type {
  ServerAlmItemTypeMap,
  ServerAlmType,
  ServerBoardIteration,
  ServerInfo,
  ServerStatusWithData,
  ServerSticky,
} from "../serverModel";
import { startServerTicks } from "../serverTime";
import { subscriptionLevels } from "./BackendReadSession";
import type { BackendReadSession, SearchStickiesQuery } from "./BackendReadSession";
import { addSticky } from "./changeSticky";
import { updateLinks } from "./linkUpdater";
import { mapAlmItemType, mapAlmItemTypes } from "./mapAlmItemType";
import {
  mapAlmStickyTypes,
  mapArtsAndTeams,
  mapBoardIterations,
  mapCategories,
  mapCompanySettings,
  mapFlexTypes,
  mapKanbanConfigs,
  mapLinks,
  mapSearchResults,
  mapSessions,
  mapSettings,
  mapStickerTypes,
  mapStickyType,
} from "./mapBackendData";
import { mapBoard, mapFlexBoard } from "./mapBoard";
import { mapObjective } from "./mapObjective";
import { mapStickyChanges } from "./mapStickyChange";
import { loadStickies } from "./stickyLoader";
import {
  createBoardSubscriptions,
  createSessionSubscriptions,
  setTimers,
} from "./subscriptionHandlers";

let backendSess: BackendReadSession = retrieveBackendSession()!;
let boardLoadScheduler: Scheduler | undefined;

/** @internal exported for tests */
export function setReaderTestSession(session: BackendReadSession) {
  backendSess = session;
}

export async function initBackendReader(session: BackendReadSession): Promise<void> {
  backendSess = session;
  void loadCompanySettings();

  boardLoadScheduler = new Scheduler();
  void startServerTicks();
  statsProvider.subscriptions = () => {
    return subscriptionLevels.reduce(
      (count, level) => count + (backendSess?.subscriptionNames(level)?.length ?? 0),
      0,
    );
  };
  const license = await backendSess.getLicense();
  const valid = license.expiryDate - Date.now() / 1000;
  useLicenseStore().setLicense({
    validDays: Math.round(valid / (60 * 60 * 24)),
    plan: license.plan,
    tracking: license.tracking,
    type: license.type,
    onPrem: license.onprem,
  });
  if (useLicenseStore().license.usable) {
    await loadSessions();
  }
}

async function loadSessions() {
  const sessions = await backendSess.getSessions();
  useSessionStore().setSessions(mapSessions(sessions));
}

export function reconnect() {
  useStatisticsStore().current.reconnects++;
  initSession(useSessionStore().current.id)
    .then(() => {
      internalActions.switchBoard();
      const board = useBoardStore().currentBoard();
      subscribeSession();
      void loadBoard(board).then(() => loadOtherBoards({ useForce: true }));
    })
    .catch(() => reload("reconnect-fail"));
}

export function getServerInfo(useHttp: boolean, timeout?: number): Promise<ServerInfo> {
  return backendSess.getServerInfo(useHttp, timeout);
}

export function getStatusWithData(
  sizeMB: number,
  useHttp?: boolean,
): Promise<ServerStatusWithData> {
  return backendSess.getStatusWithData(sizeMB, useHttp);
}

export function loggedIn(): boolean {
  return !!backendSess;
}

export function closeSession(connectionOpen?: boolean) {
  boardLoadScheduler?.cancelAll();
  if (backendSess && connectionOpen) {
    backendSess.unsubscribeLevel("room", connectionOpen).catch((err) => captureException(err));
  }
  (backendSess as unknown) = null;
}

export function initSession(sessionId: string): Promise<unknown[]> {
  useNavigationStore().setSessionId(sessionId);
  return useLoadingStore().load("setSession", doInitSession(sessionId));
}

async function doInitSession(sessionId: string): Promise<unknown[]> {
  if (sessionId.length !== 24) {
    captureException(Error(`Invalid session id ${sessionId}`));
  }
  if (!boardLoadScheduler) {
    throw Error("Session is not correctly initialized: Board Load Scheduler is undefined");
  }
  boardLoadScheduler.cancelAll();
  await backendSess.unsubscribeLevel("session");

  const isAlm = await backendSess.initSession(sessionId);
  initAlm(isAlm);
  return Promise.all(loadSession());
}

function initAlm(isAlm: boolean) {
  useSessionStore().setAlmType(backendSess.getAlmType() || "");
  useSessionStore().setUserSync("unsyncedSession");
  useSessionStore().setManualUserMapping(false);
  useConnectionStore().setAlm(true);
  if (isAlm) {
    if (getAlmInfo().isUserMapped) {
      void Promise.all([
        backendSess.hasMappedAlmUser(),
        backendSess.isManualAlmUserMappingAllowed(),
      ]).then(([mapped, manual]) => {
        useSessionStore().setUserSync(mapped ? "synced" : "unsynced");
        useSessionStore().setManualUserMapping(manual);
      });
    }
    void backendSess.getAlmStatus().then((status) => {
      useConnectionStore().setAlm(status === "running");
      useSessionStore().setSessionAlmStatus(status);
    });
  }
}

export async function loadSessionAlmStatus() {
  if (isSessionAlmSynced()) {
    const status = await backendSess.getAlmStatus();
    useSessionStore().setSessionAlmStatus(status);
  }
}

async function loadCompanySettings() {
  try {
    const settings = await backendSess.getCompanySettings();
    const mapped = mapCompanySettings(settings);
    useCompanySettingsStore().setAutoLogoutAfter(mapped.autoLogoutAfter);
  } catch (err) {
    captureException(err);
  }
}

function loadSession(): Array<Promise<unknown>> {
  return [
    backendSess
      .getSettings()
      .then((settings) => useServerSettingsStore().$patch(mapSettings(settings))),
    backendSess.getArts().then((serverArts) => {
      const { arts, teams, externals } = mapArtsAndTeams(serverArts);
      useArtStore().setArts(arts);
      useTeamStore().setTeams(teams, externals);
    }),
    backendSess.getEvents().then((events) => setTimers(events)),
  ];
}

let waitingForTab: Resolvable<boolean> | null = null;

/**
 * Loads the given board, its objectives/iterations (if applicable), and its stickies.
 *
 * @param board The board to load
 * @param options.subscribe: subscribe to updates to board & its stickies(default: true)
 * @param options.skipStickies: skip loading board's stickies (default: false)
 * @param options.skipBoardData: skip loading board's objectives (default: false)
 * @param options.skipIterations: skip loading iterations for this and related boards (default: false)
 */
export async function loadBoard(
  board: Board,
  options?: {
    subscribe?: boolean;
    skipStickies?: boolean;
    skipBoardData?: boolean;
    skipIterations?: boolean;
  },
): Promise<undefined> {
  // if we are already waiting to load a board, cancel it
  if (waitingForTab?.pending) {
    waitingForTab.reject(new CancelError("Loading the same board"));
  }
  // wait loading the board until the browser tab is active
  waitingForTab = resolveBrowserTabVisible();
  await waitingForTab.promise.then(() => (waitingForTab = null));

  await fontsLoaded();

  addBreadcrumb("load", {
    message: "Loading board",
    data: {
      id: board?.id,
      type: board?.type,
      team: assureBoardType(board, "team")?.team.name,
      tabActive: isBrowserTabActive(),
    },
  });

  // when there are no sessions available -> use EMPTY_BOARD
  // websocket reconnect then triggers loadBoard
  if (isEmptyBoard(board)) {
    return;
  }

  if (!board.id || board.id === "0") {
    throw new DataError("Board with empty id", { ...board, stickyTypes: [], cards: [] });
  }

  // TODO can probably lead to subscription events about not yet loaded data
  if (options?.subscribe !== false) {
    void backendSess.unsubscribeLevel("board");
    createBoardSubscriptions(backendSess, board);
  }

  if (isFrontendBoard(board.type)) {
    return;
  }

  if (!options?.skipBoardData) {
    if (!isBacklogBoard(board.type)) {
      const factor = await backendSess.getScale(board.id);
      board.cardSize = cardSizeFrom({ factor });
    }

    await loadBoardObjectives(board);
  }

  if (!options?.skipIterations) {
    await loadRelatedBoardIterations(board);
  }

  if (!options?.skipStickies) {
    // TODO remove when the problem is fixed
    const loadWatch = createLoadWatch(board);
    loadWatch.start();

    board.state.state = "loading";
    board.state.finishedTicks = 0;
    try {
      await doLoadStickies(backendSess, board);
      await boardStateFinished(board, "loaded");
    } catch (e) {
      if (e instanceof CancelError) {
        // session changed while loading stickies
        loadWatch.cancel();
      }
    }
  }
}

function createLoadWatch(board: Board) {
  const cardsBefore = Object.keys(board.cards).length;
  let timeout = -1;
  let canceled = false;

  function start() {
    timeout = window.setTimeout(() => {
      if (canceled) {
        return;
      }
      if (!isBrowserTabActive()) {
        return void untilBrowserTabVisible().then(start);
      }
      if (board.state.state !== "loaded") {
        captureMessage("Stickies not loaded", {
          board: { id: board.id, cardsBefore, cards: Object.keys(board.cards).length },
        });
        void boardStateFinished(board, "error");
      }
    }, 60 * SECONDS);
  }

  function cancel() {
    canceled = true;
    clearTimeout(timeout);
  }

  return { start, cancel };
}

const doLoadStickies = timeoutHandling(
  (_, board) => board.id,
  loadStickies,
  (_, board) => {
    board.state.loadedAt = 0;
    void boardStateFinished(board, "error");
  },
);

function boardStateFinished(board: Board, state: Extract<BoardState["state"], "loaded" | "error">) {
  return new Promise((resolve) => {
    sendBoardLoaded(board.id);
    board.state.state = state;
    setTimeout(() => tick(1), 100);

    function tick(i: number) {
      board.state.finishedTicks = i;
      if (i < MAX_FINISHED_TICKS) {
        void nextTick().then(() => tick(i + 1));
      } else {
        resolve(undefined);
      }
    }
  });
}

export function loadOtherBoards({ useForce = false } = {}) {
  const boards = useBoardsStore().artBoardsToLoad(useForce);
  load(0);

  function load(index: number) {
    if (!boardLoadScheduler) {
      throw Error("Session is not correctly initialized: Board Load Scheduler is undefined");
    }
    if (index < boards.length) {
      boardLoadScheduler.schedule(
        () =>
          // Load board, but skip loading iteration data for boards the user isn't looking at (performance optimization)
          // Also don't load stickies for other boards
          loadBoard(boards[index], {
            subscribe: false,
            skipStickies: true,
            skipIterations: true,
          }).then(() => load(index + 1)),
        random(50, 150),
      );
    } else {
      void updateLinks(backendSess);
    }
  }
}

export function getAlmType(): ServerAlmType {
  return backendSess.getAlmType();
}

export function getAlmInfo() {
  return almTypeInfo[backendSess.getAlmType() || ""];
}

export function isSessionAlmSynced(): boolean {
  return !!backendSess?.getAlmType();
}

export function getAlmConnectionId() {
  return backendSess.getAlmConnectionId();
}

export interface SessionData {
  boards: IdMap<Board>;
  stickyTypes: StickyType[];
  links: Link[];
  almStickyTypes: IdMap<AlmStickyType>;
  categories: Category[];
  boardIterations: IdMap<BoardIteration[]>;
  flexTypes: FlexType[];
  stickerTypes: StickerType[];
}

export function loadSessionData(session: Session): Promise<SessionData> {
  const boards: IdMap<Board> = {};
  let stickyTypes: StickyType[];
  let links: Link[];
  let almStickyTypes: IdMap<AlmStickyType>;
  let categories: Category[];
  let boardIterations: IdMap<BoardIteration[]>;
  let flexTypes: FlexType[];
  let stickerTypes: StickerType[];

  return Promise.all([
    loadLinks().then((serverLinks) => (links = mapLinks(serverLinks))),
    loadAlmStickyTypes().then((types) => (almStickyTypes = mapAlmStickyTypes(types))),
    loadCategories().then((cats) => (categories = cats)),
    loadBoardIterations(false).then((iters) => (boardIterations = iters)),
    loadStickerTypes().then((types) => (stickerTypes = mapStickerTypes(types))),
    loadStickyTypes().then((types) => {
      stickyTypes = types;
      return getFlexTypes().then((types) => {
        flexTypes = types;
        return loadBoards().then((serverBoards) => {
          serverBoards.forEach((serverBoard) => {
            boards[serverBoard.board_id] = mapBoard(
              serverBoard,
              session,
              flexTypes,
              backendSess.getAlmSource.bind(backendSess),
            );
          });
        });
      });
    }),
  ])
    .then(() => subscribeSession())
    .then(() => ({
      boards,
      stickyTypes,
      links,
      almStickyTypes,
      categories,
      boardIterations,
      flexTypes,
      stickerTypes,
    }));
}

export async function loadBoardObjectives(board: Board) {
  if (!hasBoardObjectives(board)) {
    return;
  }
  const objectives = await backendSess.getObjectives(board.id, true);
  board.objectives = objectives.objectives.map(mapObjective);
  board.stretchObjectives = objectives.stretches.map(mapObjective);
}

const { onBoardLoaded } = useEventBus({ neverUnregister: true });

export function boardLoaded(board: Board) {
  if (board.state.state === "loaded") {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    onBoardLoaded((boardId) => {
      if (boardId === board.id) {
        resolve(undefined);
      }
    });
  });
}

export async function resolveObjectives(board: BoardWithObjectives) {
  if (board.state.state !== "inited") {
    await boardLoaded(board);
  }
  const unresolved = new Set<string>();
  for (const objective of board.objectives) {
    objective.cardTree?.cards.forEach(findUnresolved);
  }
  for (const objective of board.stretchObjectives) {
    objective.cardTree?.cards.forEach(findUnresolved);
  }
  if (unresolved.size > 0) {
    const stickies = await getStickies([...unresolved]);
    stickies.forEach(addSticky);
  }

  function findUnresolved(sticky: TreeSticky) {
    if (!useCardStore().cards[sticky.id]) {
      unresolved.add(sticky.id);
    }
    sticky.children.forEach(findUnresolved);
  }
}

function loadLinks() {
  return useLoadingStore().load("links", backendSess.getLinks());
}

function loadAlmStickyTypes() {
  return useLoadingStore().load("almTypes", backendSess.getAlmStickyTypes());
}

function loadStickyTypes() {
  return useLoadingStore().load("stickyTypes", getStickyTypes());
}

function loadStickerTypes() {
  return useLoadingStore().load("stickerTypes", backendSess.getStickerTypes());
}

function loadBoards() {
  return useLoadingStore().load("boards", backendSess.getBoards());
}

function subscribeSession() {
  createSessionSubscriptions(backendSess);
}

type AlmItemTypesCache = Map<string, Promise<ServerAlmItemTypeMap>>;

export async function getStickyTypes(): Promise<StickyType[]> {
  const serverStickyTypes = await backendSess.getStickyTypes();
  const mappedServerStickyTypes = new Array<StickyType>();
  const almItemTypesCache: AlmItemTypesCache = new Map();
  while (serverStickyTypes.length) {
    const mappedStickyTypesChunk = await Promise.all(
      serverStickyTypes.splice(0, 10).map(async (serverStickyType) => {
        const loaded = useStickyTypeStore().findStickyType(serverStickyType.id);
        if (!isZombieType(loaded)) {
          return loaded;
        }
        if (serverStickyType.functionality === "workitem") {
          const almItemTypes = await getAlmItemTypes(
            serverStickyType.alm_type,
            serverStickyType.origin_board_type,
            almItemTypesCache,
          );
          useAlmItemTypeStore().setAlmItemType(serverStickyType.id, mapAlmItemTypes(almItemTypes));
        }
        return mapStickyType(serverStickyType);
      }),
    );
    mappedServerStickyTypes.push(...mappedStickyTypesChunk);
  }
  return mappedServerStickyTypes;
}

async function getFlexTypes(sessionId?: string) {
  const types = await backendSess.getFlexTypes(sessionId);
  return mapFlexTypes(types);
}

async function getAlmItemTypes(
  almType: string | null,
  boardType: BoardType,
  cache: AlmItemTypesCache,
): Promise<ServerAlmItemTypeMap> {
  const boardKeys = getBoardKeys(boardType);
  const safeAlmType = almType || "";

  const cacheKey = `${safeAlmType};${boardType};${boardKeys?.join("+")}`;
  let almItemType = cache.get(cacheKey);
  if (!almItemType) {
    almItemType = backendSess.getAlmItemType(safeAlmType, boardType, boardKeys);
    cache.set(cacheKey, almItemType);
  }
  return await almItemType;
}

function getBoardKeys(boardType: BoardType): string[] {
  switch (boardType) {
    case "backlog":
      return useArtStore().arts.map((art) => art.id);
    case "team":
      return useTeamStore().teams.map((team) => team.id);
  }
  return [];
}

export async function loadAlmItemTypeOfCard(almId: string) {
  const almItemType = await backendSess.getAlmItemTypeForSticky(almId);
  return mapAlmItemType(almItemType);
}

export async function loadFlexBoards(
  sessionId?: string,
): Promise<{ flexTypes: FlexType[]; flexBoards: Array<BoardData<"flex">> }> {
  const flexTypes = await getFlexTypes(sessionId);
  const boards = await backendSess.getBoards(sessionId);
  return {
    flexTypes,
    flexBoards: boards
      .filter((board) => board.board_type === "flex")
      .map((board) => mapFlexBoard(board, flexTypes)),
  };
}

function loadCategories(): Promise<Category[]> {
  return backendSess.getCategories().then(mapCategories);
}

function loadBoardIterations(
  withStatusDistribution: boolean,
  boardId?: string,
): Promise<Record<string, BoardIteration[]>> {
  return backendSess
    .getBoardIterations(withStatusDistribution, boardId)
    .then((iterMap: Record<string, ServerBoardIteration[]>) =>
      mapValues(iterMap, mapBoardIterations),
    );
}

async function loadRelatedBoardIterations(board: Board): Promise<void> {
  // Only load iterations for boards that show them
  if (!["team", "program", "solution"].includes(board.type)) return;

  // Load iterations with status distributions in any mode (needed to show load/capacity and shading)
  const boardIterations = await loadBoardIterations(true, board.id);

  // Update the iteration store with the loaded iterations
  useIterationStore().setBoards(boardIterations);
}

export function searchStickies(query: SearchStickiesQuery): Promise<SearchResult[]> {
  return backendSess.searchStickies(query).then(mapSearchResults);
}

export async function getBoardIdsOfGroupedStickies(stickyId: string) {
  return backendSess.getBoardIdsOfGroupedStickies(stickyId).then((res) => ({
    boardIds: res.board_ids,
    stickies: res.stickies ?? [],
  }));
}

export function getStickyChanges(id: string) {
  return backendSess.getStickyChanges(id).then(mapStickyChanges);
}

// Self-pagination limit for sticky.list requests — keeps a batch well under
// 8MB over NATS. Callers wanting per-batch error isolation chunk by this too.
export const STICKY_FETCH_BATCH_SIZE = 100;

export async function getStickies(
  ids: Array<Card["id"]>,
  withLinks = false,
  withStatusDistribution = false,
) {
  const stickies = new Array<ServerSticky>();
  let counter = 0;

  while (counter < ids.length) {
    const stickyIdsToGet = ids.slice(counter, counter + STICKY_FETCH_BATCH_SIZE);
    const stickyData = await backendSess.getStickies(
      stickyIdsToGet,
      withLinks,
      withStatusDistribution,
    );
    stickies.push(...stickyData);
    counter += STICKY_FETCH_BATCH_SIZE;
  }

  return stickies;
}

export async function getBoardState(id: string, time: Date) {
  return await backendSess.getBoardState(id, time);
}

/**
 * Returns the sum of changes made to the board's cards between the two dates.
 */
export function getBoardDiff(
  id: string,
  from: Date,
  to: Date,
  filters?: EventFilter[],
  changeTypes?: string[],
) {
  return backendSess.getBoardDiff(id, from, to, filters, changeTypes).then(mapStickyChanges);
}

/**
 * Returns
 * changes: list of all changes made to the board's cards between the two dates
 * moreChanges: boolean indicating if there are more than 1 'page' of changes
 *
 * Currently, the backend only returns the latest 100 changes. To get other results, the
 * frontend must change the to/from dates, filters, or changeTypes.
 */
export function getBoardEventLog(
  id: string,
  from: Date,
  to: Date,
  filters?: EventFilter[],
  changeTypes?: string[],
) {
  return backendSess.getBoardEventLog(id, from, to, filters, changeTypes).then((ret) => ({
    changes: mapStickyChanges(ret.results),
    moreChanges: ret.total > ret.limit,
  }));
}

export async function hasMappedAlmUser(id: string) {
  return await backendSess.hasMappedAlmUser(id);
}

export async function getArtsAndTeams(sessionId: string) {
  return backendSess.getArts(sessionId).then(mapArtsAndTeams);
}

export function loadKanbanConfigs(teamIds: string[]): Promise<Record<string, Kanban[]>> {
  return backendSess.getCachedKanbanConfigs(teamIds).then(mapKanbanConfigs);
}

// Force-refetch (bypassing the cache) the reference-board configs for the given
// teams — backs the team board's "Reload kanban configuration" action.
export function reloadKanbanConfigs(teamIds: string[]): Promise<Record<string, Kanban[]>> {
  return backendSess.getAlmKanbanConfigs(teamIds).then(mapKanbanConfigs);
}

export async function loadArtKanbanConfig(
  artId: number,
  force = false,
): Promise<Kanban | undefined> {
  const config = await backendSess.getKanbanConfig(artId, force);
  return config ? { name: config.name, columns: config.columns } : undefined;
}

export async function getStatusDistribution(params: {
  boardId?: string;
  teamId?: string;
  iterationId?: number;
  artId?: number;
  stickyId?: string;
  objectiveId?: string;
}) {
  return backendSess.getStatusDistribution(params);
}

export async function getSignals(boardId: string) {
  return backendSess.getSignals(boardId);
}

export async function getCapacityAllocation(teamIds: string[]) {
  return backendSess.getCapacityAllocation(teamIds.map(Number));
}

export async function getDependencyFlow(teamIds: string[], iterationId: number | null) {
  return backendSess.getDependencyFlow(teamIds.map(Number), iterationId);
}

export async function getBurndownChartData(teamIds: string[], scope: ChartScope) {
  return backendSess.getBurndownChartData(teamIds.map(Number), scope);
}

export async function getCfdChartData(teamIds: [string, ...string[]], scope: ChartScope) {
  return backendSess.getCfdChartData(teamIds.map(Number) as [number, ...number[]], scope);
}

export async function getPredictability(teamIds: [string, ...string[]], includeArchived: boolean) {
  return backendSess.getPredictability(
    teamIds.map(Number) as [number, ...number[]],
    includeArchived,
  );
}

export function pauseSubscriptions(pause: boolean) {
  backendSess.pauseSubscriptions(pause);
}
